import React, { useState } from 'react';
import { useTheme, makeStyles } from '@material-ui/styles';
import uploaderPlugin from './uploaderPluginCloudinary';
import AddFill from '../../icons/AddFill';
import IconButton from '../../buttons/IconButton';
import { getDefaultOptions, getCustomStyles } from './cloudinaryOptions';
import { Theme } from '../../../theme/types';

export interface UserAvatarOptions {
    onChange?: (a: string) => void;
    onSuccess?: (a: string) => void;
    onCancel?: (e: Error) => void;
    onError?: (e: Error) => void;
    config;
}

const UserAvatarUploader: React.FC<UserAvatarOptions> = ({
    onChange = () => {},
    onSuccess = () => {},
    onCancel = () => {},
    onError = () => {},
    config,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const theme = useTheme<Theme>();
    const classes = useStyles();

    const upload = async () => {
        if (isLoading) {
            return;
        }
        setIsLoading(true);
        try {
            const cloudinaryOptions = getDefaultOptions(config);
            const cloudinaryCustomStyles = getCustomStyles(theme);
            const uploader = await uploaderPlugin({
                ...cloudinaryOptions,
                ...cloudinaryCustomStyles,
                onWidgetShown: () => setIsLoading(false),
            });
            const res = await uploader();
            onSuccess(res.originUrl);
            onChange(res.url);
        } catch (e) {
            if (e instanceof Error) {
                onCancel(e);
            } else {
                onError(e);
            }
        } finally {
            setIsLoading(false);
        }
    };

    return isLoading ? (
        <div className={classes.loader} />
    ) : (
        <IconButton
            icon={AddFill}
            size="small"
            onClick={upload}
            className={classes.uploadButton}
            data-test={'attendee-avatar-uploader'}
        />
    );
};

const useStyles = makeStyles(
    theme => ({
        uploadButton: {
            mixBlendMode: 'normal',
            color: theme.palette.greys[100],
            backgroundColor: theme.palette.greys[700],
            '&:hover': {
                backgroundColor: theme.palette.greys[600],
            },
            '& svg': {
                width: 10,
            },
            [theme.breakpoints.down('sm')]: {
                width: 20,
                height: 20,
            },
        },
        loader: {
            width: '2.4rem',
            height: '2.4rem',
            borderRadius: '50%',
            border: '5px solid rgba(255, 255, 255, 0.2)',
            borderTopColor: theme.palette.greys[100],
            animation: '$spin 800ms infinite ease-in-out',
            [theme.breakpoints.down('sm')]: {
                width: 20,
                height: 20,
            },
        },
        '@keyframes spin': {
            '100%': {
                transform: 'rotate(360deg)',
            },
        },
    }),
    { classNamePrefix: 'user-avatar-loader' }
);

export default UserAvatarUploader;
